import {
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  Checkbox,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import { ChangeEvent, useCallback } from 'react';
import { Columns } from '@/types';
import { ColumnTitleTypography, TableHeadCell } from '../../Insights/EquipmentTable/styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors, primary } from '@/theme/colors';
import { TableData } from '@/types/table';
import { CustomTableIconColumn } from './CustomTableIconColumn';
import { Edit } from '@mui/icons-material';

type Props<T extends TableData> = {
  activeColumns: Columns[];
  toggleDialog: () => void;
  handleHeaderCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSort: (column: string, sortType?: 'NUMBER' | 'STRING') => void;
  orderBy: string;
  order: number;
  selectedRowIds?: string[];
  rows?: T[] | undefined;
  isChecked?: boolean;
  showGearIcon?: boolean;
  onOpenHeaderEditModal?: (columnKey: string, columnName: string) => void;
};

export const CustomTableHead = <T extends TableData>({
  activeColumns,
  toggleDialog,
  handleHeaderCheckboxChange,
  handleSort,
  orderBy,
  order,
  selectedRowIds,
  rows,
  isChecked,
  showGearIcon = true,
  onOpenHeaderEditModal,
}: Props<T>) => {
  const isView3D = useCallback(
    (index: number, key: string) => {
      return index === activeColumns.length - 1 && key === 'view3D';
    },
    [activeColumns.length]
  );

  const showIconColumn = useCallback(
    (column: Columns) => {
      return column.columnIcon?.condition ? column.columnIcon.condition(rows ?? []) : false;
    },
    [rows]
  );

  return (
    <TableHead>
      <TableRow>
        {activeColumns.map((column, index) => {
          const sortBy = column.sortKey || column.key;
          const canBulkEdit = (selectedRowIds?.length ?? 0) > 0;
          const tooltipText = canBulkEdit ? 'Edit selected rows' : 'Select rows to edit';
          const handleHeaderEditClick = () => {
            if (!canBulkEdit) return;
            if (column.key && onOpenHeaderEditModal) {
              onOpenHeaderEditModal(String(column.key), column.columnName ?? '');
            }
          };
          return (
            <TableCell
              key={column.templateFieldKey}
              sx={{
                p: 1,
                alignItems: 'center',
                position: isView3D(index, column.key ?? '') ? 'sticky' : 'relative',
                right: isView3D(index, column.key ?? '') ? 0 : 'auto',
                zIndex: isView3D(index, column.key ?? '') ? 1 : 'auto',
                boxShadow: isView3D(index, column.key ?? '')
                  ? '-4px 0px 4px 0px #0000000D'
                  : 'none',
                background: '#fff',
                '&:hover .header-edit-wrapper': {
                  opacity: 1,
                },
              }}
            >
              <TableHeadCell>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: '1 1 auto',
                      minWidth: 0,
                      gap: 0.5,
                    }}
                  >
                    {index === 0 && (
                      <Checkbox
                        onChange={handleHeaderCheckboxChange}
                        sx={{ color: primary.light, padding: { xs: 0.5, md: 0.5, lg: 1 } }}
                        checked={isChecked}
                        disableRipple
                      />
                    )}
                    <ColumnTitleTypography
                      onClick={() => {
                        if (column.isSortable) {
                          handleSort(sortBy);
                        }
                      }}
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {column.columnName}
                    </ColumnTitleTypography>

                    {showIconColumn(column) && column.columnIcon && (
                      <CustomTableIconColumn
                        selectedRowIds={selectedRowIds ?? []}
                        icon={column.columnIcon.icon}
                        action={column.columnIcon.action}
                        dialogTitle={column.columnIcon.dialogTitle}
                        dialogContent={column.columnIcon.dialogContent}
                        tooltipText={column.columnIcon.tooltipText}
                      />
                    )}

                    {
                      <TableSortLabel
                        active={orderBy === sortBy}
                        direction={order === -1 ? 'desc' : 'asc'}
                        disabled={!column.isSortable}
                        onClick={() => handleSort(sortBy)}
                        sx={{ ml: 0 }}
                      />
                    }
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      ml: 1,
                    }}
                  >
                    {column.enableInlineEdit && (
                      <Tooltip title={tooltipText} arrow className="header-edit-wrapper">
                        <Edit
                          sx={{
                            fontSize: '1.8rem',
                            cursor: canBulkEdit ? 'pointer' : 'not-allowed',
                            color: canBulkEdit ? abyssColors.primary[500] : '#9e9e9e',
                            opacity: 0,
                            transition: 'opacity 0.15s ease-in-out',
                          }}
                          aria-label={`${column.columnName}-edit`}
                          onClick={handleHeaderEditClick}
                        />
                      </Tooltip>
                    )}

                    {showGearIcon && index === activeColumns.length - 1 && (
                      <IconButton onClick={toggleDialog} sx={{ p: 1 }}>
                        <AbyssIcon iconName="gear" style={{ width: '2rem', height: '2rem' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </TableHeadCell>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
